import React, { useState, useEffect, useCallback } from 'react';
import { getCallcenterLeadsPaginated, getTeamsByOrganization } from '../api';
import { useNavigate } from 'react-router-dom';

function CallcenterLeads({ user, filter }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [availableServiceProviders, setAvailableServiceProviders] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  
  // PAGINATION STATE
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [totalLeads, setTotalLeads] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  
  const navigate = useNavigate();
  
  // Filter State - ERWEITERT mit nameSearch und localStorage-Persistenz
  const [filters, setFilters] = useState(() => {
    // Filter aus localStorage laden oder Standardwerte verwenden
    const savedFilters = localStorage.getItem('callcenterLeadsFilters');
    if (savedFilters) {
      try {
        const parsed = JSON.parse(savedFilters);
        // URL-Filter haben Vorrang über gespeicherte Filter
        if (filter === 'spaeter-anrufen') {
          return {
            ...parsed,
            sortBy: 'ruckruf_zeit',
            sortOrder: 'asc',
            status: 'Rückruf erwünscht'
          };
        } else if (filter === 'ne-kunden') {
          return {
            ...parsed,
            sortBy: 'last_call_date',
            sortOrder: 'asc',
            status: 'Nicht erreicht'
          };
        }
        return parsed;
      } catch (error) {
        console.error('Fehler beim Laden der gespeicherten Callcenter-Filter:', error);
      }
    }
    // Standardwerte basierend auf Filter
    return {
      search: '',
      sortBy: filter === 'spaeter-anrufen' ? 'ruckruf_zeit' : 
              filter === 'ne-kunden' ? 'last_call_date' : 
              'erstellt',
      sortOrder: filter === 'spaeter-anrufen' ? 'asc' : 
                 filter === 'ne-kunden' ? 'asc' : 
                 'desc',
      status: filter === 'spaeter-anrufen' ? 'Rückruf erwünscht' : 
              filter === 'ne-kunden' ? 'Nicht erreicht' : 
              filter === 'alle-kunden' ? 'Alle' : 'Alle',
      dateFrom: '',
      dateTo: '',
      statusPv: '', // Alle Status
      statusWp: '', // Alle Status
      dienstleister: '', // Dienstleister Filter
      projekt: '' // Neuer Projekt/Team Filter
    };
  });

  // Aktualisiere Filter wenn sich die URL ändert
  useEffect(() => {
    if (filter === 'ne-kunden') {
      setPage(1);
      setFilters({
        search: '',
        sortBy: 'last_call_date',
        sortOrder: 'asc',
        status: 'Nicht erreicht',
        dateFrom: '',
        dateTo: '',
        statusPv: '',
        statusWp: '',
        dienstleister: '',
        projekt: ''
      });
    } else if (filter === 'spaeter-anrufen') {
      setPage(1);
      setFilters({
        search: '',
        sortBy: 'ruckruf_zeit',
        sortOrder: 'asc',
        status: 'Rückruf erwünscht',
        dateFrom: '',
        dateTo: '',
        statusPv: '',
        statusWp: '',
        dienstleister: '',
        projekt: ''
      });
    } else {
      setPage(1);
      setFilters({
        search: '',
        sortBy: 'erstellt',
        sortOrder: 'desc',
        status: filter === 'alle-kunden' ? 'Alle' : 'Alle',
        dateFrom: '',
        dateTo: '',
        statusPv: '',
        statusWp: '',
        dienstleister: '',
        projekt: ''
      });
    }
  }, [filter]);

  // Titel und Beschreibung basierend auf Filter
  const getFilterInfo = () => {
    switch (filter) {
      case 'all':
        return {
          title: 'Alle Kunden/Leads',
          description: 'Übersicht aller Kunden und Leads in Ihrer Organisation',
          icon: '👥'
        };
      case 'open':
        return {
          title: 'Offene Leads',
          description: 'Leads die noch nicht kontaktiert wurden',
          icon: '📞'
        };
      case 'on-hold':
        return {
          title: 'On Hold',
          description: 'Leads die temporär pausiert sind',
          icon: '⏸️'
        };
      case 'termin-abgesagt':
        return {
          title: 'Termin Abgesagt',
          description: 'Kunden mit abgesagten Terminen',
          icon: '❌'
        };
      case 'ne-kunden':
        return {
          title: 'Nicht Erreichte Kunden',
          description: 'Kunden die beim letzten Anruf nicht erreicht wurden',
          icon: '📵'
        };
      case 'spaeter-anrufen':
        return {
          title: 'Später Anrufen',
          description: 'Kunden die einen Rückruf wünschen oder später kontaktiert werden sollen',
          icon: '⏰'
        };
      case 'ohne-opt-in':
        return {
          title: 'Ohne Opt In',
          description: 'Kunden ohne opt in haben',
          icon: '❌'
        };
      case 'test-leads':
        return {
          title: 'Test Leads',
          description: 'Leads mit Test Lead Status für PV oder WP',
          icon: '🧪'
        };
      case 'final-nicht-erreicht':
        return {
          title: 'Final nicht erreicht',
          description: 'Leads, die mehr als 14-mal nicht erreicht wurden',
          icon: '🚫'
        };
      case 'final-abgesagt':
        return {
          title: 'Final abgesagt',
          description: 'Leads mit Final abgesagt Status',
          icon: '❌'
        };
      default:
        return {
          title: 'Kunden/Leads',
          description: 'Kundenübersicht',
          icon: '👥'
        };
    }
  };

  // fetchLeads als useCallback definieren (angepasst für echte Paginierung)
  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Filter-Parameter für Backend vorbereiten
      let filterParams = {};
      
      // Namenssuche (serverseitig)
      if (filters.search && filters.search.trim() !== '') {
        filterParams.search = filters.search.trim();
      }
      
      // Callcenter-Filter-Typ (serverseitig)
      if (filter && filter !== 'all') {
        filterParams.filterType = filter;
      }
      
      // Status-Filter
      if (filters.statusPv) {
        filterParams.status_pv = filters.statusPv;
      }
      if (filters.statusWp) {
        filterParams.status_wp = filters.statusWp;
      }
      
      // Team/Projekt-Filter
      if (filters.projekt) {
        filterParams.projekt = filters.projekt;
      }
      
      // Dienstleister-Filter
      if (filters.dienstleister) {
        filterParams.dienstleister = filters.dienstleister;
      }
      
      // Datum-Filter
      if (filters.dateFrom) {
        filterParams.dateFrom = filters.dateFrom;
      }
      if (filters.dateTo) {
        filterParams.dateTo = filters.dateTo;
      }
      
      // --- NEU: Sortier-Parameter an das Backend übergeben ---
      filterParams.sortBy = filters.sortBy;
      filterParams.sortOrder = filters.sortOrder;
      // --- ENDE NEU ---
      
      const data = await getCallcenterLeadsPaginated(
        user.id, 
        user.organization_id, 
        limit, 
        page, 
        filterParams
      );
      
      if (data.success) {
        setLeads(data.leads);
        setTotalLeads(data.total || 0);
        setTotalPages(data.totalPages || 0);
        setHasNextPage(data.hasNextPage || false);
        setHasPrevPage(data.hasPrevPage || false);
      } else {
        setError(data.error || 'Fehler beim Abrufen der Leads');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [user.id, user.organization_id, filter, filters, limit, page]);

  // Verfügbare Dienstleister aus den Leads extrahieren
  const extractServiceProviders = useCallback((leads) => {
    // Wenn ein Team ausgewählt ist, nur Dienstleister aus diesem Team anzeigen
    const filteredLeads = filters.projekt 
      ? leads.filter(lead => lead.projekt === filters.projekt)
      : leads;

    const providers = [...new Set(
      filteredLeads
        .map(lead => lead.dienstleister)
        .filter(provider => provider && provider.trim() !== '')
    )].sort();
    
    setAvailableServiceProviders(providers);
  }, [filters.projekt]);

  // Verfügbare Teams aus allen Organisationen laden (für Callcenter)
  const loadAllTeams = useCallback(async () => {
    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('Lade alle Teams für Callcenter...');
      }
      // Für Callcenter: Alle Teams aus allen Organisationen laden
      const data = await getTeamsByOrganization(user.id, null);
      if (process.env.NODE_ENV === 'development') {
        console.log('Teams-Antwort:', data);
      }
      if (data.success) {
        const teamNames = data.teams.map(team => team.name).sort();
        if (process.env.NODE_ENV === 'development') {
          console.log('Geladene Teams:', teamNames);
        }
        setAvailableTeams(teamNames);
      } else {
        console.error('Fehler beim Laden der Teams:', data.error);
      }
    } catch (err) {
      console.error('Fehler beim Laden der Teams:', err);
    }
  }, [user.id]);

  // Verfügbare Teams aus den Leads extrahieren (Fallback)
  const extractTeams = useCallback((leads) => {
    const teams = [...new Set(
      leads
        .map(lead => lead.projekt)
        .filter(team => team && team.trim() !== '' && !team.includes('Wichtige Teams'))
    )].sort();
    
    setAvailableTeams(teams);
  }, []);

  // Clientseitige Filterung entfernt - alles wird serverseitig gemacht


  
  // Filter-Handler
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [filterName]: value
      };

      // Wenn das Team geändert wird, den Dienstleister zurücksetzen
      if (filterName === 'projekt') {
        newFilters.dienstleister = '';
      }

      return newFilters;
    });
  };

  // Filter zurücksetzen - ERWEITERT
  const resetFilters = () => {
    setFilters({
      search: '',
      sortBy: filter === 'spaeter-anrufen' ? 'ruckruf_zeit' : 
              filter === 'ne-kunden' ? 'last_call_date' : 
              'erstellt',
      sortOrder: filter === 'spaeter-anrufen' ? 'asc' : 
                 filter === 'ne-kunden' ? 'asc' : 
                 'desc',
      status: filter === 'spaeter-anrufen' ? 'Rückruf erwünscht' : 
              filter === 'ne-kunden' ? 'Nicht erreicht' : 
              filter === 'alle-kunden' ? 'Alle' : 'Alle',
      dateFrom: '',
      dateTo: '',
      statusPv: '',
      statusWp: '',
      dienstleister: '',
      projekt: ''
    });
  };

  // NEUE FUNKTION: Suchfeld leeren
  const clearNameSearch = () => {
    setFilters(prev => ({
      ...prev,
      search: ''
    }));
  };

  const formatLastCallTime = (lastCallDate) => {
    if (!lastCallDate) return '-';

    try {
      const date = new Date(lastCallDate);
      if (isNaN(date.getTime())) return '-';

      // Formatierung: DD.MM.YYYY HH:MM
      return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Fehler beim Formatieren der Anruf-Zeit:', error);
      return '-';
    }
  };

  const showLastCallTimeColumn = filter === 'ne-kunden';

  // Leads laden, wenn Filter sich ändern
  useEffect(() => {
    fetchLeads();
  }, [filters, fetchLeads]);

  // NEUER HOOK: Automatisches Neuladen der Leads alle 30 Sekunden
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchLeads();
      
      // Nur in Entwicklung loggen
      if (process.env.NODE_ENV === 'development') {
        console.log('Leads wurden neu geladen:', new Date().toLocaleString('de-DE'), {
          anzahlLeads: leads.length,
          currentPage: page,
          totalPages: totalPages
        });
      }
    }, 30000); // 30 Sekunden

    // Cleanup: Interval löschen wenn Komponente unmounted wird
    return () => clearInterval(intervalId);
  }, [fetchLeads, leads, page, totalPages]);

  // Teams beim ersten Laden abrufen (für Callcenter alle Teams)
  useEffect(() => {
    loadAllTeams();
  }, [loadAllTeams]);

  // Dienstleister aus den aktuellen Leads extrahieren
  useEffect(() => {
    if (leads.length > 0) {
      extractServiceProviders(leads);
    }
  }, [leads, extractServiceProviders, filters.projekt]);

  // Paginierung-Funktionen
  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const goToNextPage = () => {
    if (hasNextPage) {
      setPage(prev => prev + 1);
    }
  };

  const goToPrevPage = () => {
    if (hasPrevPage) {
      setPage(prev => prev - 1);
    }
  };

  // Wenn Filter sich ändern, Seite zurück auf 1
  useEffect(() => {
    setPage(1);
  }, [filters, filter, limit]);

  // Filter im localStorage speichern, wenn sie sich ändern
  useEffect(() => {
    localStorage.setItem('callcenterLeadsFilters', JSON.stringify(filters));
  }, [filters]);

  // Status-Farben-Funktion
  const getStatusColor = (status) => {
    if (!status) return "";
    
    status = status.toLowerCase();
    if (status.includes('offen') || status.includes('neu')) return "status-new";
    if (status.includes('bearbeitung') || status.includes('in arbeit')) return "status-in-progress";
    if (status.includes('abgeschlossen') || status.includes('fertig')) return "status-completed";
    if (status.includes('qualifiziert') || status.includes('bestätigt')) return "status-completed";
    if (status.includes('abgelehnt') || status.includes('storniert') || status.includes('kein interesse') || status.includes('widerruf') || status.includes('ungültig') || status.includes('test')) return "status-cancelled";
    return "";
  };

  // Status-Rendering mit Validierung und Farben
  const renderStatus = (status, productType, leadProduct) => {
    const hasProduct = leadProduct && leadProduct.toLowerCase().includes(productType.toLowerCase());
    
    if (!hasProduct) {
      return <span>-</span>;
    }
    
    if (!status || status.trim() === '') {
      return <span className="status-badge status-new">Offen</span>;
    }
    
    return (
      <span className={`status-badge ${getStatusColor(status)}`}>
        {status}
      </span>
    );
  };

  // Anruf-Status Badge
  const renderCallStatus = (lead) => {
    if (process.env.NODE_ENV === 'development') {
      console.log("lead1312 : ", lead);
    }
    if (!lead.has_calls || lead.call_count === 0) {
      if (lead.opt_in === 0 && (lead.signature === null || lead.signature === "") ) {
        return <span className="call-badge call-no-interest">Ohne OptIn</span>;
      } else {
        return <span className="call-badge call-new">Nicht kontaktiert</span>;
      }
    }
    
    
    if (lead.last_call_status) {
      const statusColors = {
        'Nicht erreicht': 'call-not-reached',
        'Rückruf erwünscht': 'call-callback',
        'Später anrufen': 'call-callback',
        'Termin abgesagt': 'call-cancelled',
        'On Hold': 'call-hold',
        'Qualifiziert': 'call-qualified',
        'Kein Interesse': 'call-no-interest',
      };
      
      const colorClass = statusColors[lead.last_call_status] || 'call-default';
      
      return (
        <span className={`call-badge ${colorClass}`}>
          {lead.last_call_status}
        </span>
      );
    }
    
    return <span className="call-badge call-contacted">Kontaktiert</span>;
  };

  // Rückruf-Zeit formatieren
  const formatCallbackTime = (ruckrufZeit) => {
    if (!ruckrufZeit) return '-';
    
    try {
      const date = new Date(ruckrufZeit);
      
      // Prüfen ob das Datum gültig ist
      if (isNaN(date.getTime())) return '-';
      
      // Formatierung: DD.MM.YYYY HH:MM
      return date.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Fehler beim Formatieren der Rückruf-Zeit:', error);
      return '-';
    }
  };

  const handleCustomerSelect = (customerId, e) => {
    if (e) {
      // Bei Ctrl+Klick oder mittlerer Maustaste in neuem Tab öffnen
      if (e.ctrlKey || e.button === 1) {
        e.preventDefault();
        window.open(`/customer/${customerId}`, '_blank');
        return;
      }
      // Bei normalem Linksklick im gleichen Tab öffnen
      if (e.button === 0) {
        e.preventDefault();
        navigate(`/customer/${customerId}`);
      }
    }
  };

  // Hilfsfunktion für Console-Logs beim Navigieren zu Customer Details
  const handleCustomerNavigation = (lead, e) => {
    // Console-Log für die Lead-Daten (nur in Entwicklung)
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 Lead-Daten beim Klick:', {
        id: lead.id,
        name: lead.name,
        email: lead.email,
        produkt: lead.produkt,
        dienstleister: lead.dienstleister,
        projekt: lead.projekt,
        status_pv: lead.status_pv,
        status_wp: lead.status_wp,
        customer_status: lead.customer_status,
        erstellt: lead.erstellt,
        ruckruf_zeit: lead.ruckruf_zeit,
        last_call_date: lead.last_call_date
      });

      // Console-Log für die Organisations-ID
      console.log('🏢 Organisations-ID:', user.organization_id);
    }

    // Normale Navigation durchführen
    if (!e.ctrlKey && e.button === 0) {
      e.preventDefault();
      navigate(`/customer/${lead.id}`);
    }
  };

  const filterInfo = getFilterInfo();

  // Prüfen ob Rückruf-Zeit Spalte angezeigt werden soll (nur bei "Später Anrufen" Filter)
  const showCallbackTimeColumn = filter === 'spaeter-anrufen';

  return (
    <div className="callcenter-leads-page">
      <div className="leads-header">
        <div className="header-title">
          <span className="header-icon">{filterInfo.icon}</span>
          <div>
            <h2>{filterInfo.title}</h2>
            <p className="header-description">{filterInfo.description}</p>
          </div>
        </div>
        
        <div className="leads-count">
          <span className="count-number">{leads.length}</span>
          <span className="count-label">{leads.length === 1 ? 'Lead' : 'Leads'}</span>
          {/* NEUE ANZEIGE: Suchstatus */}
          {filters.search && (
            <div className="search-indicator">
              <span className="search-text">🔍 "{filters.search}"</span>
            </div>
          )}
        </div>
      </div>

      {/* Filter-Bereich - ERWEITERT mit Namenssuche */}
      <div className="leads-filters">
        {/* NEUE SUCHZEILE */}
        <div className="search-row">
          <div className="search-group search-group-flex">
            <label style={{ marginRight: 12 }}>🔍 Namenssuche:</label>
            <div className="search-input-flex">
              <input
                type="text"
                placeholder="Name eingeben..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="search-input"
              />
              {filters.search && (
                <button 
                  className="clear-search-button"
                  onClick={clearNameSearch}
                  title="Suche löschen"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          
          {filters.search && (
            <div className="search-results-info">
              {leads.length === 0 ? (
                <span className="no-results">Keine Ergebnisse für "{filters.search}"</span>
              ) : (
                <span className="results-count">
                  {leads.length} Ergebnis{leads.length !== 1 ? 'se' : ''} für "{filters.search}"
                </span>
              )}
            </div>
          )}
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <label>Sortieren nach:</label>
            <select 
              value={filters.sortBy} 
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="sort-select"
            >
              <option value="erstellt">Erstellt</option>
              <option value="name">Name</option>
              {filter === 'ne-kunden' && <option value="last_call_date">Letzter Anruf</option>}
              {filter === 'spaeter-anrufen' && <option value="ruckruf_zeit">Rückruf-Zeit</option>}
            </select>
          </div>
          
          <div className="filter-group">
            <label>Reihenfolge:</label>
            <select 
              value={filters.sortOrder} 
              onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
            >
              <option value="desc">Absteigend</option>
              <option value="asc">Aufsteigend</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Dienstleister:</label>
            <select 
              value={filters.dienstleister}
              onChange={(e) => handleFilterChange('dienstleister', e.target.value)}
            >
              <option value="">Alle Dienstleister</option>
              {availableServiceProviders.map(provider => (
                <option key={provider} value={provider}>
                  {provider}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Projekt:</label>
            <select 
              value={filters.projekt}
              onChange={(e) => handleFilterChange('projekt', e.target.value)}
            >
              <option value="">Alle Teams</option>
              {availableTeams.map(team => (
                <option key={team} value={team}>
                  {team}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="filter-row">
          <div className="filter-group">
            <label>Von Datum:</label>
            <input 
              type="date" 
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            />
          </div>
          
          <div className="filter-group">
            <label>Bis Datum:</label>
            <input 
              type="date" 
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            />
          </div>
          
          <div className="filter-group">
            <label>PV Status:</label>
            <select 
              value={filters.statusPv} 
              onChange={(e) => handleFilterChange('statusPv', e.target.value)}
            >
              <option value="">Alle Status</option>
              <option value="Offen">Offen</option>
              <option value="Qualifiziert">Qualifiziert</option>
              <option value="Kein Interesse mehr">Kein Interesse mehr</option>
              <option value="Widerruf">Widerruf</option>
              <option value="Telefonnummer ungültig">Telefonnummer ungültig</option>
              <option value="Test Lead">Test Lead</option>
              <option value="Kwh zu niedrig">Kwh zu niedrig</option>
              <option value="Denkmalschutz">Denkmalschutz</option>
              <option value="Dach muss saniert werden">Dach muss saniert werden</option>
              <option value="Technisch nicht möglich">Technisch nicht möglich</option>
              <option value="Dublette">Dublette</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>WP Status:</label>
            <select 
              value={filters.statusWp} 
              onChange={(e) => handleFilterChange('statusWp', e.target.value)}
            >
              <option value="">Alle Status</option>
              <option value="Offen">Offen</option>
              <option value="Qualifiziert">Qualifiziert</option>
              <option value="Kein Interesse mehr">Kein Interesse mehr</option>
              <option value="Widerruf">Widerruf</option>
              <option value="Telefonnummer ungültig">Telefonnummer ungültig</option>
              <option value="Test Lead">Test Lead</option>
              <option value="Kwh zu niedrig">Kwh zu niedrig</option>
              <option value="Denkmalschutz">Denkmalschutz</option>
              <option value="Dach muss saniert werden">Dach muss saniert werden</option>
              <option value="Technisch nicht möglich">Technisch nicht möglich</option>
              <option value="Dublette">Dublette</option>
            </select>
          </div>
          
          <div className="filter-group">
            <button className="reset-filters-button" onClick={resetFilters}>
              Alle Filter zurücksetzen
            </button>
          </div>
        </div>
      </div>
      {error && <div className="error-message">{error}</div>}
      {loading ? (
        <div className="loading">Leads werden geladen...</div>
      ) : leads.length === 0 ? (
        <div className="no-data">
          {filters.search ? (
            <div>
              <p>Keine Leads mit dem Namen "<strong>{filters.search}</strong>" gefunden.</p>
              <button onClick={clearNameSearch} className="clear-search-suggestion">
                Suche löschen und alle {filterInfo.title.toLowerCase()} anzeigen
              </button>
            </div>
          ) : Object.values(filters).some(filter => filter !== '' && filter !== 'erstellt' && filter !== 'desc') ? (
            'Keine Leads mit den aktuellen Filtern gefunden.' 
          ) : (
            `Keine ${filterInfo.title.toLowerCase()} gefunden.`
          )}
        </div>
      ) : (
        <div className="table-container">
          <table className="leads-table">
            <thead>
              <tr>
                <th>Erstellt</th>
                <th>Produkt</th>
                <th>Anruf-Status</th>
                {showCallbackTimeColumn && <th>Rückruf-Zeit</th>}
                {showLastCallTimeColumn && <th>Letzter Anruf</th>}
                <th>Dienstleister</th>
                <th>Name</th>
                <th>E-Mail</th>
                <th>Handy</th>
                <th>Status PV</th>
                <th>Status WP</th>
                <th>Projekt</th>
                <th style={{ minWidth: '500px' }}>Team</th>
              </tr>
            </thead>

            <tbody>
              {leads.map((lead) => (
                <tr 
                  key={lead.id} 
                  style={{ cursor: 'pointer' }}
                  className={`${filters.search && `${lead.anrede || ''} ${lead.name || ''} ${lead.nachname || ''}`.toLowerCase().includes(filters.search.toLowerCase()) ? 'search-highlight' : ''} ${Number(lead.agentur_id) === 64 ? 'lead-row-agentur-64' : (lead.organization_name === 'Energie+' ? 'lead-row-org-6' : '')}`}
                >
                  <td>
                    <a 
                      href={`/customer/${lead.id}`}
                      onClick={(e) => handleCustomerNavigation(lead, e)}
                      style={{ 
                        display: 'block', 
                        color: 'inherit', 
                        textDecoration: 'none',
                        height: '100%',
                        width: '100%'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        {new Date(lead.erstellt).toLocaleString('de-DE', {
                          dateStyle: 'short',
                          timeStyle: 'short'
                        })}
                      </div>
                    </a>
                  </td>

                  <td>
                    <a 
                      href={`/customer/${lead.id}`}
                      onClick={(e) => handleCustomerNavigation(lead, e)}
                      style={{ 
                        display: 'block', 
                        color: 'inherit', 
                        textDecoration: 'none',
                        height: '100%',
                        width: '100%'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        {lead.produkt ? (
                          <div className="product-badges">
                            {lead.produkt.toLowerCase().includes('pv') && (
                              <span className="product-badge pv-badge">PV</span>
                            )}
                            {lead.produkt.toLowerCase().includes('wp') && (
                              <span className="product-badge wp-badge">WP</span>
                            )}
                          </div>
                        ) : (
                          '-'
                        )}
                      </div>
                    </a>
                  </td>

                  <td>
                    <a 
                      href={`/customer/${lead.id}`}
                      onClick={(e) => handleCustomerNavigation(lead, e)}
                      style={{ 
                        display: 'block', 
                        color: 'inherit', 
                        textDecoration: 'none',
                        height: '100%',
                        width: '100%'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        {renderCallStatus(lead)}
                      </div>
                    </a>
                  </td>

                  {showCallbackTimeColumn && (
                    <td>
                      <a 
                        href={`/customer/${lead.id}`}
                        onClick={(e) => handleCustomerNavigation(lead, e)}
                        style={{ 
                          display: 'block', 
                          color: 'inherit', 
                          textDecoration: 'none',
                          height: '100%',
                          width: '100%'
                        }}
                      >
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          height: '100%',
                          fontSize: '0.85em', 
                          color: lead.ruckruf_zeit ? '#4a6de5' : '#718096',
                          fontWeight: lead.ruckruf_zeit ? '500' : 'normal'
                        }}>
                          {formatCallbackTime(lead.ruckruf_zeit)}
                        </div>
                      </a>
                    </td>
                  )}

                  {showLastCallTimeColumn && (
                    <td>
                      <a 
                        href={`/customer/${lead.id}`}
                        onClick={(e) => handleCustomerNavigation(lead, e)}
                        style={{ 
                          display: 'block', 
                          color: 'inherit', 
                          textDecoration: 'none',
                          height: '100%',
                          width: '100%'
                        }}
                      >
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          height: '100%',
                          fontSize: '0.85em', 
                          color: lead.last_call_date ? '#e53e3e' : '#718096',
                          fontWeight: lead.last_call_date ? '500' : 'normal'
                        }}>
                          {formatLastCallTime(lead.last_call_date)}
                        </div>
                      </a>
                    </td>
                  )}

                  <td>
                    <a 
                      href={`/customer/${lead.id}`}
                      onClick={(e) => handleCustomerNavigation(lead, e)}
                      style={{ 
                        display: 'block', 
                        color: 'inherit', 
                        textDecoration: 'none',
                        height: '100%',
                        width: '100%'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        {lead.dienstleister || '-'}
                      </div>
                    </a>
                  </td>

                  <td>
                    <a 
                      href={`/customer/${lead.id}`}
                      onClick={(e) => handleCustomerNavigation(lead, e)}
                      style={{ 
                        display: 'block', 
                        color: 'inherit', 
                        textDecoration: 'none',
                        height: '100%',
                        width: '100%'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        <span className={filters.search && `${lead.anrede || ''} ${lead.name || ''} ${lead.nachname || ''}`.toLowerCase().includes(filters.search.toLowerCase()) ? 'name-highlight' : ''}>
                          {`${lead.anrede ? lead.anrede + ' ' : ''}${lead.name || ''} ${lead.nachname || ''}`.trim()}
                        </span>
                      </div>
                    </a>
                  </td>

                  <td>
                    <a 
                      href={`/customer/${lead.id}`}
                      onClick={(e) => handleCustomerNavigation(lead, e)}
                      style={{ 
                        display: 'block', 
                        color: 'inherit', 
                        textDecoration: 'none',
                        height: '100%',
                        width: '100%'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        {lead.email || '-'}
                      </div>
                    </a>
                  </td>

                  <td>
                    <a 
                      href={`/customer/${lead.id}`}
                      onClick={(e) => handleCustomerNavigation(lead, e)}
                      style={{ 
                        display: 'block', 
                        color: 'inherit', 
                        textDecoration: 'none',
                        height: '100%',
                        width: '100%'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        {lead.handy || '-'}
                      </div>
                    </a>
                  </td>

                  <td>
                    <a 
                      href={`/customer/${lead.id}`}
                      onClick={(e) => handleCustomerNavigation(lead, e)}
                      style={{ 
                        display: 'block', 
                        color: 'inherit', 
                        textDecoration: 'none',
                        height: '100%',
                        width: '100%'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        {renderStatus(lead.status_pv, 'pv', lead.produkt)}
                      </div>
                    </a>
                  </td>

                  <td>
                    <a 
                      href={`/customer/${lead.id}`}
                      onClick={(e) => handleCustomerNavigation(lead, e)}
                      style={{ 
                        display: 'block', 
                        color: 'inherit', 
                        textDecoration: 'none',
                        height: '100%',
                        width: '100%'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        {renderStatus(lead.status_wp, 'wp', lead.produkt)}
                      </div>
                    </a>
                  </td>

                  <td>
                    <a 
                      href={`/customer/${lead.id}`}
                      onClick={(e) => handleCustomerNavigation(lead, e)}
                      style={{ 
                        display: 'block', 
                        color: 'inherit', 
                        textDecoration: 'none',
                        height: '100%',
                        width: '100%'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        {lead.organization_name || '-'}
                      </div>
                    </a>
                  </td>

                  <td>
                    <a 
                      href={`/customer/${lead.id}`}
                      onClick={(e) => handleCustomerNavigation(lead, e)}
                      style={{ 
                        display: 'block', 
                        color: 'inherit', 
                        textDecoration: 'none',
                        height: '100%',
                        width: '100%'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                        {lead.projekt || '-'}
                      </div>
                    </a>
                  </td>        

                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginierung */}
      {!loading && leads.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            <span>Seite {page} von {totalPages}</span>
            <span>•</span>
            <span>{totalLeads} Leads insgesamt</span>
          </div>
          
          <div className="pagination-controls">
            <button 
              className="pagination-btn" 
              onClick={goToPrevPage}
              disabled={!hasPrevPage}
            >
              ← Zurück
            </button>
            
            <div className="page-numbers">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    className={`page-number ${page === pageNum ? 'active' : ''}`}
                    onClick={() => goToPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button 
              className="pagination-btn" 
              onClick={goToNextPage}
              disabled={!hasNextPage}
            >
              Weiter →
            </button>
          </div>
          
          <div className="leads-per-page">
            <label>Leads pro Seite:</label>
            <select 
              value={limit} 
              onChange={(e) => setLimit(parseInt(e.target.value))}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={150}>150</option>
              <option value={200}>200</option>
            </select>
          </div>
        </div>
      )}

      {/* Erweiterte Styles */}
      <style jsx>{`
        .callcenter-leads-page {
          padding: 20px;
          max-width: 100%;
          overflow: hidden; /* Änderung von auto zu hidden */
        }

        .leads-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 1px solid #e2e8f0;
          position: relative;
        }

        .header-title {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .header-icon {
          font-size: 2rem;
        }

        .header-title h2 {
          margin: 0;
          color: #2d3748;
          font-size: 1.5rem;
        }

        .header-description {
          margin: 4px 0 0 0;
          color: #718096;
          font-size: 0.875rem;
        }

        .leads-count {
          display: flex;
          flex-direction: column;
          align-items: center;
          background: #f7fafc;
          padding: 12px 20px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .count-number {
          font-size: 1.5rem;
          font-weight: 700;
          color: #4a6de5;
        }

        .count-label {
          font-size: 0.75rem;
          color: #718096;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        /* NEUE STYLES FÜR SUCHE */
        .search-indicator {
          margin-top: 8px;
          font-size: 0.7rem;
          color: #4a6de5;
          background: #ebf4ff;
          padding: 2px 8px;
          border-radius: 4px;
        }

        .search-row {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 16px;
          padding: 16px;
          background: #f8fafc;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
        }

        .search-group-flex {
          display: flex;
          flex-direction: row;
          align-items: center;
          min-width: 0;
          flex: 1;
        }

        .search-input-flex {
          display: flex;
          flex-direction: row;
          align-items: center;
          flex: 1;
          min-width: 0;
          gap: 8px;
          max-width: 400px;
          width: 100%;
        }

        .search-input {
          flex: 1 1 0;
          min-width: 0;
          width: auto;
          padding: 10px 12px;
          border: 2px solid #e2e8f0;
          border-radius: 6px;
          font-size: 0.95rem;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .search-input:focus {
          outline: none;
          border-color: #4a6de5;
          box-shadow: 0 0 0 3px rgba(74, 109, 229, 0.1);
        }

        .clear-search-button {
          background: none;
          border: none;
          color: #a0aec0;
          cursor: pointer;
          font-size: 1.1rem;
          width: 32px;
          height: 32px;
          min-width: 32px;
          min-height: 32px;
          max-width: 32px;
          max-height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s, color 0.2s;
          margin-left: 0;
        }

        .clear-search-button:hover {
          background-color: #f7fafc;
          color: #e53e3e;
        }

        .search-results-info {
          display: flex;
          align-items: center;
          font-size: 0.875rem;
        }

        .no-results {
          color: #e53e3e;
          font-weight: 500;
        }

        .results-count {
          color: #38a169;
          font-weight: 500;
        }

        .clear-search-suggestion {
          background: #4a6de5;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          margin-top: 8px;
          transition: background-color 0.2s;
        }

        .clear-search-suggestion:hover {
          background: #3c5ce0;
        }

        /* HIGHLIGHT STYLES */
        .search-highlight {
          background-color: #fff5f5 !important;
        }

        .search-highlight:hover {
          background-color: #fed7d7 !important;
        }

        .name-highlight {
          background-color: #fef3c7;
          padding: 2px 4px;
          border-radius: 3px;
          font-weight: 600;
        }

        .call-badge {
          display: inline-block;
          padding: 0.25rem 0.5rem;
          border-radius: 0.375rem;
          font-size: 0.75rem;
          font-weight: 500;
          text-align: center;
          white-space: nowrap;
        }

        .call-new {
          background-color: #fef3c7;
          color: #d97706;
        }

        .call-not-reached {
          background-color: #fed7d7;
          color: #e53e3e;
        }

        .call-callback {
          background-color: #c3dafe;
          color: #3182ce;
        }

        .call-cancelled {
          background-color: #fed7d7;
          color: #e53e3e;
        }

        .call-hold {
          background-color: #e2e8f0;
          color: #4a5568;
        }

        .call-qualified {
          background-color: #c6f6d5;
          color: #2f855a;
        }

        .call-no-interest {
          background-color: #fed7d7;
          color:rgb(148, 2, 2);
        }

        .call-contacted {
          background-color: #d4edda;
          color: #28a745;
        }

        .call-default {
          background-color: #e2e8f0;
          color: #4a5568;
        }

        /* FILTER STYLES */
        .leads-filters {
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          padding: 20px;
          margin-bottom: 20px;
        }

        .filter-row {
          display: flex;
          gap: 20px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          min-width: 150px;
          flex: 1;
        }

        .filter-group label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #4a5568;
          margin-bottom: 6px;
        }

        .filter-group select,
        .filter-group input {
          padding: 8px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-size: 0.875rem;
          transition: border-color 0.2s;
        }

        .filter-group select:focus,
        .filter-group input:focus {
          outline: none;
          border-color: #4a6de5;
          box-shadow: 0 0 0 3px rgba(74, 109, 229, 0.1);
        }

        .reset-filters-button {
          padding: 8px 16px;
          background-color: #f3f4f6;
          color: #4b5563;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          margin-top: 22px;
          transition: all 0.2s;
        }

        .reset-filters-button:hover {
          background-color: #e5e7eb;
          color: #1f2937;
        }

        /* TABLE STYLES */
        .leads-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          table-layout: fixed;
        }

        /* Neuer Container für die Tabelle mit Scrollbalken */
        .table-container {
          position: relative;
          width: 100%;
          overflow-x: auto;
          overflow-y: auto;
          max-height: calc(100vh - 300px); /* Anpassbare Höhe */
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        /* Scrollbalken-Styling */
        .table-container::-webkit-scrollbar {
          height: 12px;
          width: 12px;
        }

        .table-container::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 6px;
        }

        .table-container::-webkit-scrollbar-thumb {
          background: #cbd5e0;
          border-radius: 6px;
        }

        .table-container::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }

        /* Doppelte Header für bessere Lesbarkeit beim Scrollen */
        .leads-table thead {
          position: sticky;
          top: 0;
          z-index: 1;
        }

        .leads-table thead th {
          position: sticky;
          top: 0;
          background: #f7fafc;
          z-index: 2;
        }

        .leads-table th {
          background: #f7fafc;
          padding: 8px 12px; /* Reduzierte Polsterung */
          text-align: left;
          font-weight: 600;
          color: #4a5568;
          border-bottom: 1px solid #e2e8f0;
          font-size: 0.75rem; /* Kleinere Schriftgröße */
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .leads-table td {
          padding: 8px 12px; /* Reduzierte Polsterung */
          border-bottom: 1px solid #e2e8f0;
          vertical-align: middle;
          font-size: 0.75rem; /* Kleinere Schriftgröße */
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Spezifische Spaltenbreiten */
        .leads-table th:nth-child(1), /* Erstellt */
        .leads-table td:nth-child(1) {
          width: 120px;
        }

        .leads-table th:nth-child(2), /* Produkt */
        .leads-table td:nth-child(2) {
          width: 80px;
        }

        .leads-table th:nth-child(3), /* Anruf-Status */
        .leads-table td:nth-child(3) {
          width: 140px;
        }

        .leads-table th:nth-child(4), /* Rückruf-Zeit */
        .leads-table td:nth-child(4) {
          width: 140px;
        }

        .leads-table th:nth-child(5), /* Letzter Anruf */
        .leads-table td:nth-child(5) {
          width: 140px;
        }

        .leads-table th:nth-child(6), /* Dienstleister */
        .leads-table td:nth-child(6) {
          width: 120px;
        }

        .leads-table th:nth-child(7), /* Name */
        .leads-table td:nth-child(7) {
          width: 150px;
        }

        .leads-table th:nth-child(8), /* E-Mail */
        .leads-table td:nth-child(8) {
          width: 150px;
        }

        .leads-table th:nth-child(9), /* Handy */
        .leads-table td:nth-child(9) {
          width: 120px;
        }

        .leads-table th:nth-child(10), /* Status PV */
        .leads-table td:nth-child(10) {
          width: 120px;
        }

        .leads-table th:nth-child(11), /* Status WP */
        .leads-table td:nth-child(11) {
          width: 120px;
        }

        .leads-table th:nth-child(12), /* Projekt */
        .leads-table td:nth-child(12) {
          width: 120px;
        }

        /* PRODUCT BADGES */
        .product-badges {
          display: flex;
          gap: 4px;
        }

        .product-badge {
          display: inline-block;
          padding: 0.2rem 0.5rem;
          border-radius: 0.25rem;
          font-size: 0.7rem;
          font-weight: 700;
          text-align: center;
        }

        .pv-badge {
          background-color: #c3dafe;
          color: #4a6de5;
        }

        .wp-badge {
          background-color: #b2f5ea;
          color: #3182ce;
        }

        /* STATUS BADGES */
        .status-badge {
          display: inline-block;
          padding: 0.25rem 0.5rem;
          border-radius: 0.375rem;
          font-size: 0.75rem;
          font-weight: 500;
          text-align: center;
          white-space: nowrap;
        }

        .status-new {
          background-color: #ebf8ff;
          color: #3182ce;
        }

        .status-in-progress {
          background-color: #fefcbf;
          color: #d69e2e;
        }

        .status-completed {
          background-color: #c6f6d5;
          color: #38a169;
        }

        .status-cancelled {
          background-color: #fed7d7;
          color: #e53e3e;
        }

        /* UTILITY STYLES */
        .loading {
          text-align: center;
          padding: 40px;
          color: #718096;
          font-size: 1rem;
        }

        .error-message {
          color: #e53e3e;
          background-color: #fed7d7;
          padding: 12px;
          border-radius: 6px;
          margin-bottom: 16px;
          text-align: center;
        }

        .no-data {
          text-align: center;
          padding: 40px;
          color: #a0aec0;
          font-size: 1rem;
        }

        /* RESPONSIVE DESIGN */
        @media (max-width: 768px) {
          .search-row {
            flex-direction: column;
            gap: 12px;
          }

          .search-group-flex {
            min-width: auto;
          }

          .filter-row {
            flex-direction: column;
            gap: 12px;
          }

          .filter-group {
            min-width: auto;
          }

          .leads-table {
            font-size: 0.8rem;
          }

          .leads-table th,
          .leads-table td {
            padding: 8px 6px;
          }
        }

        /* ACCESSIBILITY */
        .search-input:focus,
        .filter-group select:focus,
        .filter-group input:focus {
          outline: none;
          border-color: #4a6de5;
          box-shadow: 0 0 0 3px rgba(74, 109, 229, 0.1);
        }

        .reset-filters-button:focus,
        .clear-search-button:focus,
        .clear-search-suggestion:focus {
          outline: 2px solid #4a6de5;
          outline-offset: 2px;
        }

        /* TOP PAGINATION STYLES */
        .top-pagination {
          position: absolute;
          top: 0;
          right: 0;
        }

        .page-navigation {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 8px 12px;
        }

        .page-nav-btn {
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          border-radius: 4px;
          padding: 6px 10px;
          cursor: pointer;
          font-size: 0.875rem;
          min-width: 32px;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .page-nav-btn:hover:not(:disabled) {
          background: #e5e7eb;
          border-color: #9ca3af;
        }

        .page-nav-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .page-info {
          font-size: 0.875rem;
          font-weight: 500;
          color: #4a5568;
          padding: 0 8px;
          min-width: 60px;
          text-align: center;
        }

        /* TABLE HEADER CONTROLS */
        .table-header-controls {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 16px;
          padding: 12px 0;
          border-bottom: 1px solid #e2e8f0;
        }

        .leads-per-page {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .leads-per-page label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #4a5568;
          white-space: nowrap;
        }

        .leads-per-page select {
          padding: 6px 10px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-size: 0.875rem;
          background: white;
          cursor: pointer;
        }

        .leads-per-page select:focus {
          outline: none;
          border-color: #4a6de5;
          box-shadow: 0 0 0 3px rgba(74, 109, 229, 0.1);
        }

        /* RESPONSIVE DESIGN */
        @media (max-width: 768px) {
          .leads-header {
            flex-direction: column;
            align-items: stretch;
            gap: 16px;
          }

          .top-pagination {
            position: static;
            align-self: flex-end;
          }

          .page-navigation {
            padding: 6px 8px;
            gap: 4px;
          }

          .page-nav-btn {
            padding: 4px 8px;
            font-size: 0.8rem;
            min-width: 28px;
          }

          .page-info {
            font-size: 0.8rem;
            min-width: 50px;
          }

          .table-header-controls {
            justify-content: center;
          }

          .leads-per-page {
            flex-direction: column;
            gap: 4px;
            align-items: center;
          }
        }

        /* PAGINATION STYLES */
        .pagination-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          background: white;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
          margin-top: 20px;
        }

        .pagination-info {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.875rem;
          color: #4a5568;
        }

        .pagination-controls {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .pagination-btn {
          padding: 8px 16px;
          background: #4a6de5;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: background-color 0.2s;
        }

        .pagination-btn:hover:not(:disabled) {
          background: #3c5ce0;
        }

        .pagination-btn:disabled {
          background: #cbd5e0;
          cursor: not-allowed;
        }

        .page-numbers {
          display: flex;
          gap: 4px;
        }

        .page-number {
          padding: 8px 12px;
          background: #f7fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          color: #2d3748;
          transition: all 0.2s;
        }

        .page-number:hover {
          background: #edf2f7;
          border-color: #cbd5e0;
        }

        .page-number.active {
          background: #4a6de5;
          color: white;
          border-color: #4a6de5;
        }

        .leads-per-page {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .leads-per-page label {
          font-size: 0.875rem;
          color: #4a5568;
        }

        .leads-per-page select {
          padding: 6px 10px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-size: 0.875rem;
          background: white;
        }

        /* RESPONSIVE PAGINATION */
        @media (max-width: 768px) {
          .pagination-container {
            flex-direction: column;
            gap: 16px;
            text-align: center;
          }

          .pagination-controls {
            order: 1;
          }

          .pagination-info {
            order: 2;
          }

          .leads-per-page {
            order: 3;
          }
        }
      `}</style>
    </div>
  );
}

export default CallcenterLeads;